import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ApiResponse,
  HistoryAction,
  PageableDto,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { mapPageableContent } from '../../../utils/map-pageable';

/**
 * Servicio dedicado al historial de acciones de una empresa.
 *
 * Endpoints:
 *   - GET  /api/structure/companies/{id}/history?page=&size=&sort=&direction=
 *   - GET  /api/export/pdf/history/company/{id}?title=
 *   - GET  /api/export/excel/history/company/{id}?title=
 */
@Injectable({ providedIn: 'root' })
export class CompanyHistoryService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/structure/companies`;
  }

  getHistory(
    id: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
  ): Observable<HistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    return this.httpClient
      .get<ApiResponse<PageableDto<HistoryAction>>>(
        `${this.apiUrl}/${id}/history`,
        { params },
      )
      .pipe(map((response) => mapPageableContent(response)));
  }

  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de empresas',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/company/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de empresas',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/company/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
